// src/hooks/useLeadNotifications.js
import { useEffect }   from 'react';
import { io }          from 'socket.io-client';
import { toast }       from 'react-toastify';
import { saveUnprocessedJob } from '../components/MyLinkJobPopup';

// Helper function to format date/time like My Diary
const formatWhen = (iso) => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date in formatWhen:', iso);
      return 'Invalid Date';
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      weekday: 'short', day: 'numeric', month: 'short',
      timeZone: 'Europe/London'
    }).formatToParts(date);
    const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
    return `${p.hour}:${p.minute} ${p.dayPeriod?.toLowerCase()}, ${p.weekday} ${p.day} ${p.month}`;
  } catch (error) {
    console.warn('Error formatting date:', iso, error);
    return 'Invalid Date';
  }
};

// Helper function to show flash notification
// eslint-disable-next-line no-unused-vars
const showFlashNotification = (urgency = 'normal') => {
  const isUrgent = urgency === 'urgent';
  
  // Create a flash overlay
  const flashOverlay = document.createElement('div');
  flashOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: ${isUrgent ? 'rgba(220, 53, 69, 0.9)' : 'rgba(40, 167, 69, 0.8)'};
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${isUrgent ? 'urgentFlash' : 'flash'} 0.5s ease-in-out;
  `;
  
  // Add flash animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes flash {
      0%, 100% { opacity: 0; }
      50% { opacity: 1; }
    }
    @keyframes urgentFlash {
      0%, 100% { opacity: 0; transform: scale(0.95); }
      50% { opacity: 1; transform: scale(1.05); }
    }
  `;
  document.head.appendChild(style);
  
  // Add text
  const text = document.createElement('div');
  text.textContent = isUrgent ? '🚨 URGENT: New Job Request Arrived!' : 'New Job Request Arrived!';
  text.style.cssText = `
    color: white;
    font-size: ${isUrgent ? '28px' : '24px'};
    font-weight: bold;
    text-align: center;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    animation: ${isUrgent ? 'pulseText 1s infinite' : 'none'};
  `;
  flashOverlay.appendChild(text);
  
  // Add pulse animation for urgent jobs
  if (isUrgent) {
    const pulseStyle = document.createElement('style');
    pulseStyle.textContent = `
      @keyframes pulseText {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    `;
    document.head.appendChild(pulseStyle);
    setTimeout(() => {
      if (document.head.contains(pulseStyle)) {
        document.head.removeChild(pulseStyle);
      }
    }, 3000);
  }
  
  document.body.appendChild(flashOverlay);
  
  // Remove after animation (longer for urgent)
  setTimeout(() => {
    if (document.body.contains(flashOverlay)) {
      document.body.removeChild(flashOverlay);
    }
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  }, isUrgent ? 3000 : 2000);
};

// Note: Audio is now handled by the MyLinkJobPopup component

export default function useLeadNotifications() {
  useEffect(() => {
    const myId = localStorage.getItem('userId');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const socket = io(baseUrl, {
      // Allow polling fallback to avoid early close during upgrade on some hosts
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 5000
    });

    socket.on('connect',    () => console.log('🔌 Socket connected:', socket.id));
    socket.on('disconnect', () => console.log('❌ Socket disconnected:', socket.id));
    socket.on('connect_error', (err) => {
      console.warn('Socket connect_error:', err?.message || err);
    });

    // 1) New public lead submissions
    socket.on('new-lead', data => {
      // public submits have initiatedBy === null, so everyone gets notified
      // but if we ever wire-up authenticated submits, no one sees their own
      if (data.initiatedBy === myId) return;
      // Show enhanced lead notification with timing and customer info
      const timingInfo = data.dateTime ? 
        `📅 ${formatWhen(data.dateTime)}` : 
        '⚡ ASAP';
      
      const customerInfo = data.customerName ? `👤 ${data.customerName}` : '';
      
      toast.info(
        `🚖 New Lead\n${timingInfo}\n📍 ${data.from} → ${data.to}${customerInfo ? `\n${customerInfo}` : ''}`, 
        {
          autoClose: false,
          style: {
            whiteSpace: 'pre-line'
          }
        }
      );
    });

    // 2) Any operator passing a lead back into pending  
    socket.on('lead-released', data => {
      // ignore your own release
      if (data.initiatedBy === myId) return;
      toast.warn(`🔄 Lead released: ${data.from} → ${data.to}`, {
        autoClose: false
      });
    });

    // 3) New booking request from MyLink
    socket.on('new-booking-request', data => {
      // Only notify the specific driver
      if (data.driverId === myId) {
        const isASAP = data.isASAP || false;
        
        // DO NOT show flash notification - popup system handles MyLink jobs
        // showFlashNotification(isASAP ? 'urgent' : 'normal'); // REMOVED - using popup instead
        
        // Save job to localStorage for popup system
        const jobData = {
          leadId: data.leadId,
          _id: data.leadId,
          from: data.from,
          to: data.to,
          customerName: data.customerName,
          passengers: data.passengers,
          dateTime: data.dateTime,
          isASAP: isASAP,
          notes: data.notes || '',
          bookingLinkId: data.bookingLinkId
        };
        
        console.log('📱 MyLink booking request received, saving to localStorage:', jobData);
        saveUnprocessedJob(jobData);
        
        // Emit custom event to trigger popup immediately
        console.log('📢 Emitting mylink-new-booking-request event');
        window.dispatchEvent(new CustomEvent('mylink-new-booking-request', {
          detail: jobData
        }));
        
        // Emit custom event to refresh diary data
        window.dispatchEvent(new CustomEvent('refreshDiaryData', {
          detail: { leadId: data.leadId, from: data.from, to: data.to, isASAP: isASAP }
        }));
      }
    });

    // 4) Diary entry added
    socket.on('diary-entry-added', data => {
      // Only notify the specific driver
      if (data.driverId === myId) {
        console.log('📅 Diary entry added:', data);
        
        // Emit custom event to refresh diary data
        window.dispatchEvent(new CustomEvent('refreshDiaryData', {
          detail: { leadId: data.leadId, from: data.from, to: data.to, type: 'diary-entry' }
        }));
      }
    });

    return () => socket.disconnect();
  }, []);
}
